import { useEffect, useState } from "react";
import { previewInvitation, acceptInvitation, AUTH_TOKEN_KEY } from "@/lib/api";
import { Compass as CompassIcon, Loader2, Eye, EyeOff } from "lucide-react";

// Invitation landing (/?invite=<token>) — the ONLY way a teacher account is created. Possession of the
// single-use token proves email ownership; an existing account is upgraded, a new one is registered.
export default function AcceptInvite({ token }) {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    previewInvitation(token).then(setInv).catch(() => setInv({ valid: false, reason: "This invitation link is not valid." }))
      .finally(() => setLoading(false));
  }, [token]);

  const finish = (res) => {
    localStorage.setItem(AUTH_TOKEN_KEY, res.token);
    localStorage.setItem("compass_auth_user", JSON.stringify(res.user));
    window.location.href = "/?classroom";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!inv.existing_account && password.length < 10) { setError("Choose a password of at least 10 characters."); return; }
    setBusy(true);
    try {
      finish(await acceptInvitation(token, inv.existing_account ? {} : { name, password }));
    } catch (err) {
      setError(err.response?.data?.detail || "Could not accept this invitation.");
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-stone-300 rounded-sm px-3 py-2.5 text-[14px] focus:border-[#8C3A2A] outline-none transition-colors";
  const label = "block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel";

  return (
    <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-6" data-testid="accept-invite-screen">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-md shadow-sm p-8">
        <div className="flex items-center gap-2 text-[#8C3A2A] mb-1">
          <CompassIcon className="h-5 w-5" />
          <span className="font-mono-panel text-[11px] uppercase tracking-[0.18em]">The Writing Studio</span>
        </div>
        {loading ? (
          <p className="text-[14px] text-stone-500 py-6" data-testid="invite-loading">Checking your invitation…</p>
        ) : !inv?.valid ? (
          <div className="py-4" data-testid="invite-invalid">
            <h1 className="font-serif-display text-2xl text-stone-900 mb-2">Invitation unavailable</h1>
            <p className="text-[14px] text-stone-600 mb-6">{inv?.reason}</p>
            <a href="/?classroom" data-testid="invite-goto-signin" className="text-[13.5px] text-[#8C3A2A] hover:underline">Go to sign in</a>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display text-2xl text-stone-900 mb-1">You&rsquo;re invited{inv.role === "teacher" ? " to teach" : ""}</h1>
            <p className="text-[13.5px] text-stone-600 mb-5" data-testid="invite-summary">
              {inv.invited_by || "Your administrator"} invited <b className="font-semibold">{inv.email}</b> as a <b className="font-semibold">{inv.role}</b>
              {inv.course_title ? <> for <b className="font-semibold">{inv.course_title}</b></> : null}.
            </p>
            <form onSubmit={submit} className="space-y-3">
              {inv.existing_account ? (
                <p className="text-[13px] text-stone-500" data-testid="invite-existing-note">You already have an account with this email. Accepting will activate it{inv.role === "teacher" ? " as a teacher account" : ""}.</p>
              ) : (
                <>
                  <div>
                    <label className={label}>Your name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="How students should see you" data-testid="invite-name-input" className={inputCls} />
                  </div>
                  <div className="relative">
                    <label className={label}>Choose a password</label>
                    <input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 10 characters" data-testid="invite-password-input" className={inputCls + " pr-10"} />
                    <button type="button" onClick={() => setShow((s) => !s)} data-testid="invite-password-toggle"
                      className="absolute right-2.5 top-[34px] text-stone-400 hover:text-[#8C3A2A] transition-colors">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </>
              )}
              {error && <p data-testid="invite-error" className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{error}</p>}
              <button type="submit" disabled={busy} data-testid="invite-accept-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Accept invitation
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
